// The last good data, saved on the device so the next visit can show it at once
// while a refresh runs in the background. One snapshot per country, for the
// two most recently used countries; snapshots older than a week are ignored.
// A full storage quota is ignored silently. Each league has its own snapshots
// (the AHL's keys have an "AHL_" part; the NHL keeps the original keys).

import type { Division, Game, Player, PointsByDate } from '../model/types';

export const SNAPSHOT_KEY_PREFIX = 'nhlcountries_cache_v1_';
export const SNAPSHOT_MAX_AGE_MS = 7 * 24 * 3600 * 1000;
const KEPT_COUNTRIES = 2;

export interface Snapshot {
    country: string;
    players: Player[];
    points: PointsByDate;
    scores: Game[];
    standings: Division[];
    updateId: string;
    lastUpdated?: number | string;
    savedAt: number;
}

const read = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const write = (key: string, value: string): boolean => {
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
};

const remove = (key: string) => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // nothing to do
    }
};

const readIndex = (indexKey: string): string[] => {
    try {
        const parsed: unknown = JSON.parse(read(indexKey) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
        return [];
    }
};

const isSnapshot = (value: unknown, country: string): value is Snapshot => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Partial<Snapshot>;
    return candidate.country === country
        && typeof candidate.updateId === 'string'
        && typeof candidate.savedAt === 'number'
        && Array.isArray(candidate.players)
        && Array.isArray(candidate.scores)
        && Array.isArray(candidate.standings)
        && typeof candidate.points === 'object' && candidate.points !== null;
};

export interface SnapshotStore {
    load(country: string, now?: number): Snapshot | undefined;
    save(snapshot: Snapshot): void;
}

// namespace: '' for the NHL, 'AHL_' for the AHL.
export function snapshotStore(namespace: string): SnapshotStore {
    const prefix = SNAPSHOT_KEY_PREFIX + namespace;
    const indexKey = `${prefix}index`;

    return {
        load(country, now = Date.now()) {
            const raw = read(prefix + country);
            if (raw === null) {
                return undefined;
            }
            try {
                const parsed: unknown = JSON.parse(raw);
                if (!isSnapshot(parsed, country) || now - parsed.savedAt > SNAPSHOT_MAX_AGE_MS) {
                    return undefined;
                }
                return parsed;
            } catch {
                return undefined;
            }
        },
        save(snapshot) {
            if (!write(prefix + snapshot.country, JSON.stringify(snapshot))) {
                return;
            }
            const recent = [snapshot.country, ...readIndex(indexKey).filter(country => country !== snapshot.country)];
            recent.slice(KEPT_COUNTRIES).forEach(country => remove(prefix + country));
            write(indexKey, JSON.stringify(recent.slice(0, KEPT_COUNTRIES)));
        },
    };
}

const nhlStore = snapshotStore('');
export const loadSnapshot = nhlStore.load;
export const saveSnapshot = nhlStore.save;
